import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { Select, message } from 'antd';
import { combineLatest } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import Moment from 'moment';
import * as XLSX from 'xlsx-js-style';

import { userAssociatedFacilities } from '../../../common/services/ApiService';
import { getRecallCallListReport, getRecallCallListData } from '../../../common/services/next-gen/ReportService';

const SELECT_ALL = 'Select All';

const RECALL_TYPES = ['Screening Mammogram', 'Diagnostic Ultrasound', 'Diagnostic Mammogram'];

const RETURN_STATUSES = [
    { id: 'Overdue', name: 'Overdue' },
    { id: 'DueWithin30Days', name: 'Due Within 30 Days' }
];

const statusLabel = (id) => (RETURN_STATUSES.find(s => s.id === id) || {}).name || id;

class RecallCallListReportModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            showModal: props.showModal,
            allFacilities: [],
            selectedFacilities: [],
            selectedRecallTypes: [...RECALL_TYPES],
            selectedReturnStatuses: RETURN_STATUSES.map(s => s.id),
            loading: false
        };

        this.alive = false;
    }

    componentDidMount() {
        this.alive = true;

        combineLatest(
            userAssociatedFacilities
        ).pipe(takeWhile(() => this.alive)).subscribe(([f]) => {
            if (f?.length > 0) {
                this.setState({
                    allFacilities: f,
                    selectedFacilities: f.length === 1 ? [f[0].facilityModel.facilityID] : [],
                    loaded: true
                });
            }
        });
    }

    componentWillUnmount() {
        this.alive = false;
    }

    handleFacilityChange = (selectedFacilities) => {
        const allIds = this.state.allFacilities.map(f => f.facilityModel.facilityID);
        const hadSelectAll = this.state.selectedFacilities.includes(SELECT_ALL);
        const hasSelectAll = selectedFacilities.includes(SELECT_ALL);

        if (hasSelectAll && !hadSelectAll) {
            this.setState({ selectedFacilities: [SELECT_ALL, ...allIds] });
        } else if (!hasSelectAll && hadSelectAll) {
            this.setState({ selectedFacilities: [] });
        } else {
            const individual = selectedFacilities.filter(f => f !== SELECT_ALL);
            const all = allIds.length > 0 && allIds.every(id => individual.includes(id));
            this.setState({ selectedFacilities: all ? [SELECT_ALL, ...individual] : individual });
        }
    }

    handleClear = () => {
        this.setState({
            selectedFacilities: this.state.allFacilities.length === 1 ? [this.state.allFacilities[0].facilityModel.facilityID] : [],
            selectedRecallTypes: [...RECALL_TYPES],
            selectedReturnStatuses: RETURN_STATUSES.map(s => s.id)
        });
    }

    buildRequest = () => {
        const selectedFacilities = this.state.selectedFacilities.filter(f => f !== SELECT_ALL);

        if (selectedFacilities.length === 0) {
            message.warning('At least one facility is required');
            return null;
        }
        if (this.state.selectedRecallTypes.length === 0) {
            message.warning('At least one recall type is required');
            return null;
        }
        if (this.state.selectedReturnStatuses.length === 0) {
            message.warning('At least one return status is required');
            return null;
        }

        return {
            selectedFacilities,
            selectedRecallTypes: this.state.selectedRecallTypes,
            returnStatuses: this.state.selectedReturnStatuses,
            ianaTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
    }

    handleSubmit = async () => {
        const request = this.buildRequest();
        if (!request) return;

        const key = 'recallCallListPdf';
        message.loading({ content: 'Generating Volume Recapture Report...', key, duration: 0 });
        this.setState({ loading: true });
        try {
            const blob = await getRecallCallListReport(request);
            const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
            window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
            message.destroy(key);
        } catch (error) {
            console.error('Error generating Volume Recapture Report:', error);
            message.error({ content: 'Failed to generate report. Please try again.', key });
        } finally {
            this.setState({ loading: false });
        }
    }

    exportExcel = async () => {
        const request = this.buildRequest();
        if (!request) return;

        const key = 'recallCallListExcel';
        message.loading({ content: 'Exporting Volume Recapture Report...', key, duration: 0 });
        this.setState({ loading: true });
        try {
            const rows = await getRecallCallListData(request);

            const headers = ['Return Status', 'Patient Name', 'Home Phone', 'Cell Phone', 'Work Phone', 'Work Ext', 'Exam Date', 'Recall Date', 'Recall Type'];
            const colCount = headers.length;
            const plain = (v) => ({ v });

            const facilityNames = request.selectedFacilities
                .map(id => {
                    const f = this.state.allFacilities.find(x => x.facilityModel.facilityID === id);
                    return f ? f.facilityModel.facilityName : id;
                })
                .join(', ');

            const criteriaText = [
                'Search Criteria:',
                `Facilities: ${facilityNames}`,
                `Recall Types: ${request.selectedRecallTypes.join(', ')}`,
                `Return Status: ${request.returnStatuses.map(statusLabel).join(', ')}`
            ].join('\n');
            const criteriaRowCount = 4;
            const criteriaRows = [
                [{ v: criteriaText, s: { font: { bold: true }, alignment: { wrapText: true, vertical: 'top' } } }, ...new Array(colCount - 1).fill(plain(''))],
                ...new Array(criteriaRowCount - 1).fill(new Array(colCount).fill(plain('')))
            ];
            const merges = [{ s: { r: 0, c: 0 }, e: { r: criteriaRowCount - 1, c: colCount - 1 } }];

            const blankRow = new Array(colCount).fill({ v: '' });
            const headerRow = headers.map(h => ({ v: h, s: { font: { bold: true }, alignment: { horizontal: 'center', vertical: 'center' } } }));
            const formatDate = (d) => (d ? Moment(d).format('M/D/YYYY') : '');

            const dataRows = rows.map(r => [
                statusLabel(r.returnStatus),
                r.patientName,
                r.homePhone || '',
                r.cellPhone || '',
                r.workPhone || '',
                r.workPhoneExt || '',
                formatDate(r.examDate),
                formatDate(r.recallDate),
                r.recallType
            ]);

            const ws = XLSX.utils.aoa_to_sheet([...criteriaRows, blankRow, headerRow, ...dataRows]);
            ws['!merges'] = merges;
            ws['!cols'] = [20, 28, 16, 16, 16, 10, 12, 12, 24].map(wch => ({ wch }));

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Volume Recapture Report');
            XLSX.writeFile(wb, `Volume_Recapture_Report_${Moment().format('YYYY-MM-DD')}.xlsx`);

            message.destroy(key);
        } catch (error) {
            console.error('Error exporting Volume Recapture Report:', error);
            message.error({ content: 'Failed to export. Please try again.', key });
        } finally {
            this.setState({ loading: false });
        }
    }

    render() {
        const { Option } = Select;
        const theme = window.sessionStorage.getItem('theme');

        return (
            <Modal size='lg' show={this.state.showModal} backdrop='static' dialogClassName={theme === 'dark' ? 'dark-theme' : 'light-theme'}>
                <Modal.Header>
                    <h1>Volume Recapture Report</h1>
                </Modal.Header>
                <Modal.Body className="no-y-overflow">
                    <div className="panel-content">
                        {this.state.loaded &&
                            <div className="row">
                                <div className="form-group col col-12">
                                    <div className="form-label">Facility</div>
                                    {this.state.allFacilities.length === 1 ?
                                        <div>{this.state.allFacilities[0].facilityModel.facilityName}</div>
                                        :
                                        <Select
                                            mode="multiple"
                                            maxTagCount="responsive"
                                            allowClear
                                            placeholder="Please select"
                                            onChange={this.handleFacilityChange}
                                            className="form-control-custom w-100"
                                            bordered={false}
                                            virtual={false}
                                            value={this.state.selectedFacilities}
                                            showSearch
                                            filterOption={(input, option) => (option.children[0] + option.children[1]).toLowerCase().includes(input.toLowerCase())}
                                        >
                                            <Option key={SELECT_ALL} value={SELECT_ALL}>{SELECT_ALL}</Option>
                                            {this.state.allFacilities.map(fac => <Option key={fac.facilityModel.facilityID} value={fac.facilityModel.facilityID}>{fac.facilityModel.facilityNumber && fac.facilityModel.facilityNumber + " - "}{fac.facilityModel.facilityName}</Option>)}
                                        </Select>
                                    }
                                </div>
                                <div className="form-group col col-lg-6 col-12">
                                    <div className="form-label">Recall Type</div>
                                    <Select
                                        mode="multiple"
                                        maxTagCount="responsive"
                                        allowClear
                                        placeholder="Please select"
                                        onChange={(selectedRecallTypes) => this.setState({ selectedRecallTypes })}
                                        className="form-control-custom w-100"
                                        bordered={false}
                                        virtual={false}
                                        value={this.state.selectedRecallTypes}
                                    >
                                        {RECALL_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                                    </Select>
                                </div>
                                <div className="form-group col col-lg-6 col-12">
                                    <div className="form-label">Return Status</div>
                                    <Select
                                        mode="multiple"
                                        maxTagCount="responsive"
                                        allowClear
                                        placeholder="Please select"
                                        onChange={(selectedReturnStatuses) => this.setState({ selectedReturnStatuses })}
                                        className="form-control-custom w-100"
                                        bordered={false}
                                        virtual={false}
                                        value={this.state.selectedReturnStatuses}
                                    >
                                        {RETURN_STATUSES.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                                    </Select>
                                </div>
                            </div>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-submit float-left" onClick={this.handleSubmit} disabled={this.state.loading} style={{ left: '10px', position: 'fixed', backgroundColor: '#1D6F42', borderColor: '#1D6F42' }}>Export to PDF</button>
                    <div className="row">
                        <div className="form-group col-12 padding-25-10">
                            <button className="btn btn-submit" onClick={this.exportExcel} disabled={this.state.loading}>Run Report</button>
                            <button className="btn btn-outline-default margin-left-15" onClick={this.handleClear} disabled={this.state.loading}>Clear</button>
                            <button className="btn btn-outline-default margin-left-15" onClick={() => this.props.hideModal()}>Cancel</button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default withRouter(RecallCallListReportModal);
